import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContext'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import BottomNav from '../components/BottomNav'
import { getNucleusScope } from '../utils/nucleusScope'
import { useCoordinatorMode } from '../context/CoordinatorModeContext'
import { ACTIVITY_TYPES } from '../constants'
import { recalculateProgress } from '../utils/recalculateProgress'
import { mergeTwoParticipants } from '../utils/mergeTwoParticipants'
import { detectMergeConflicts } from '../utils/detectMergeConflicts'
import { writeBlocked } from '../utils/writeBlocked'
import ConflictResolutionModal from '../components/ConflictResolutionModal'

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const d = new Date(dateStr + 'T12:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function Home() {
  const { profile, signOut, user } = useAuth()
  const { coordinatorMode } = useCoordinatorMode()
  const navigate = useNavigate()
  const [activities, setActivities] = useState([])
  const [nuclei, setNuclei] = useState([])
  const [coreNuclei, setCoreNuclei] = useState([])
  const [sessionCount, setSessionCount] = useState(0)
  const [protagonistCount, setProtagonistCount] = useState(0)
  const [participantCount, setParticipantCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [matchSuggestions, setMatchSuggestions] = useState([])
  const [myPendingSuggestion, setMyPendingSuggestion] = useState(null)
  const [showMatchModal, setShowMatchModal] = useState(false)
  const [resolvingSuggestion, setResolvingSuggestion] = useState(false)
  const [recentSessions, setRecentSessions] = useState([])
  const [showAllSessions, setShowAllSessions] = useState(false)
  const [oneOffEvents, setOneOffEvents] = useState([])
  const [showAllEvents, setShowAllEvents] = useState(false)
  const [showEventsPanel, setShowEventsPanel] = useState(false)
  const [meetings, setMeetings] = useState([])
  const [showAllMeetings, setShowAllMeetings] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [showAllActivities, setShowAllActivities] = useState(false)
  const [nucleusFilter, setNucleusFilter] = useState(null) // null = all
  const [activeCampaigns, setActiveCampaigns] = useState([])
  const [showArchive, setShowArchive] = useState(false)
  const [dismissedAutoLink, setDismissedAutoLink] = useState(false)
  const [scanLoading, setScanLoading] = useState(false)
  const [myResolvedSuggestions, setMyResolvedSuggestions] = useState([])
  // Conflict-resolution modal state. When set, renders the modal and
  // pauses the merge until the user picks per-field winners.
  // Shape: { keepId, dropId, keep, drop, conflicts, suggestion }
  const [pendingMerge, setPendingMerge] = useState(null)

  const today = new Date()
  const dateStr = today.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

  const location = useLocation()
  // Stale-fetch guard. fetchAll runs twice on mount: once with the
  // useState default coordinatorMode=false, then again when
  // CoordinatorModeContext resolves the DB flag to true. The two runs
  // race, and the slower (false) run can win the setState — overwriting
  // the coord's "all nuclei" list with their own-memberships subset.
  // This is the disappearing-nuclei symptom (and was the same root cause
  // behind the campaigns flashing fix in fetchActiveCampaigns). Each
  // fetchAll claims a token; scope-derived commits no-op if a newer run
  // has started. RLS-backed reads (activities, campaigns, sessions) are
  // already race-safe server-side and don't need the guard.
  const fetchTokenRef = useRef(0)
  useEffect(() => { fetchAll() }, [location.key, coordinatorMode, nucleusFilter])

  const fetchAll = async () => {
    const token = ++fetchTokenRef.current
    const isStale = () => token !== fetchTokenRef.current
    setLoading(true)
    // Get nucleus scope first — all teammate user/participant IDs
    const scope = await getNucleusScope(user.id, coordinatorMode)

    // Fetch pending match suggestions for coordinators. Gated on the
    // coordinatorMode context, not profile.coordinator_mode_active —
    // the profile is loaded via AuthContext and can lag the toggle by
    // one re-render; the context value is the authoritative live state.
    if (coordinatorMode) {
      const { data: rawSuggestions } = await supabase
        .from('profile_match_suggestions')
        .select('id, similarity_score, status, profile_id, suggested_participant_id, new_participant_id')
        .eq('status', 'pending')
        .order('created_at', { ascending: false })

      if (rawSuggestions?.length) {
        // Fetch all related participant and profile IDs separately to avoid RLS join issues
        const partIds = [...new Set([
          ...rawSuggestions.map(s => s.suggested_participant_id),
          ...rawSuggestions.map(s => s.new_participant_id),
        ].filter(Boolean))]
        const profileIds = rawSuggestions.map(s => s.profile_id).filter(Boolean)

        // Check 8 tables for any connection — informs "safe to delete" banner
        // and Delete button gate. Queries cover both new + suggested sides so the
        // swap toggle can swap flags without an async re-fetch. Includes profile
        // linkage (profiles.participant_id) so an auth-linked record can never be
        // shown as "safe to delete" on the DROP side — deleting would orphan auth.
        const tieQuery = (table, col) => partIds.length > 0
          ? supabase.from(table).select(col).in(col, partIds)
          : { data: [] }

        const [
          { data: parts }, { data: profs },
          { data: attRows }, { data: npRows }, { data: apRows },
          { data: pncRows }, { data: hhRows }, { data: regRows }, { data: noteRows },
          { data: profLinkRows },
        ] = await Promise.all([
          // Read via participants_v: email masks to NULL for unauthorized viewers (per migration 30).
          supabase.from('participants_v').select('id, first_name, last_name, email, added_by').in('id', partIds),
          supabase.from('profiles').select('id, full_name').in('id', profileIds),
          partIds.length > 0
            ? supabase.from('session_attendance').select('participant_id').in('participant_id', partIds).eq('present', true)
            : { data: [] },
          tieQuery('nucleus_participants', 'participant_id'),
          tieQuery('activity_participants', 'participant_id'),
          tieQuery('participant_nucleus_connections', 'participant_id'),
          tieQuery('household_members', 'participant_id'),
          tieQuery('registrations', 'matched_participant_id'),
          tieQuery('participant_notes', 'participant_id'),
          tieQuery('profiles', 'participant_id'),   // auth-linked participant — hard "keep" signal
        ])

        const partMap = Object.fromEntries((parts || []).map(p => [p.id, p]))
        const profMap = Object.fromEntries((profs || []).map(p => [p.id, p]))

        // Aggregate "has connections" — 7 tables keyed on participant_id, 1 on matched_participant_id
        const hasConnections = new Set()
        ;[attRows, npRows, apRows, pncRows, hhRows, noteRows, profLinkRows].forEach(rows =>
          (rows || []).forEach(r => hasConnections.add(r.participant_id))
        )
        ;(regRows || []).forEach(r => hasConnections.add(r.matched_participant_id))

        const enrichedBase = rawSuggestions.map(s => ({
          ...s,
          profile: profMap[s.profile_id] || null,
          suggested: partMap[s.suggested_participant_id] || null,
          new_participant: partMap[s.new_participant_id] || null,
          newHasHistory: hasConnections.has(s.new_participant_id),
          suggestedHasHistory: hasConnections.has(s.suggested_participant_id),
        }))

        // Phase D commit 6: fetch the top-3 candidates per suggestion via
        // find_similar_participants (mig 33 already returns up to 3).
        // Coord can override the trigger's first pick before merging.
        // SECURITY INVOKER on the RPC means coord-mode RLS sees all
        // un-deleted participants — same scope as the merge action itself.
        const candidatesPerSuggestion = await Promise.all(
          enrichedBase.map(async (s) => {
            const fn = s.new_participant?.first_name
            const ln = s.new_participant?.last_name
            if (!fn) return []
            const { data, error } = await supabase.rpc('find_similar_participants', {
              p_first: fn, p_last: ln || '',
            })
            if (error) {
              console.warn('find_similar_participants failed for suggestion', s.id, error)
              return []
            }
            // Exclude the new_participant itself from candidates — it appears
            // because the RPC scans all participants. Coord wants alternatives.
            return (data || []).filter(c => c.id !== s.new_participant_id)
          })
        )

        const enriched = enrichedBase.map((s, i) => {
          const candidates = candidatesPerSuggestion[i] || []
          // Default the picker to the trigger's stored pick if it appears
          // in the live top-3; else fall back to the first candidate; else
          // keep the original suggested_participant_id (no picker shown).
          const triggerInList = candidates.some(c => c.id === s.suggested_participant_id)
          const _pickedId = triggerInList
            ? s.suggested_participant_id
            : (candidates[0]?.id || s.suggested_participant_id)
          return { ...s, candidates, _pickedId, _isSwapped: false }
        })
        // Defense-in-depth: never show a suggestion whose records can't both be
        // resolved (e.g. one side hard-deleted → new_participant_id nulled, mig
        // 135). Such a card renders "Unknown" and its Merge always cancels.
        const showable = enriched.filter(s => s.new_participant && s.suggested)
        setMatchSuggestions(showable)
      } else {
        setMatchSuggestions([])
      }
    }

    // Fetch all suggestions for this user — pending, resolved, or approved
    const { data: mySuggestionsRaw } = await supabase
      .from('profile_match_suggestions')
      .select('id, status, suggested_participant_id, duplicate_name, resolved_at')
      .eq('profile_id', user.id)
      .in('status', ['pending', 'rejected', 'approved'])
    const mySuggestions = mySuggestionsRaw || []
    const myPending = mySuggestions.find(s => s.status === 'pending') || null
    // Keep-Separate (see handleRejectMerge below, ~line 549) is the only write path
    // that sets status='rejected' without also setting duplicate_name. Suppress those
    // from the resolved-notification feed — both records still exist by design, so the
    // "was removed" wording would mislead the field worker. Convention: Blueprint §10.4.
    const myResolved = mySuggestions.filter(s =>
      s.status === 'approved' || (s.status === 'rejected' && s.duplicate_name)
    )

    // Enrich pending suggestion with real person's name
    if (myPending?.suggested_participant_id) {
      const { data: suggestedPart } = await supabase
        .from('participants')
        .select('first_name, last_name')
        .eq('id', myPending.suggested_participant_id)
        .maybeSingle()
      setMyPendingSuggestion({ ...myPending, suggested: suggestedPart || null })
    } else {
      setMyPendingSuggestion(myPending)
    }

    // Enrich resolved suggestions with real person's name
    if (myResolved.length > 0) {
      const resolvedPartIds = myResolved.map(s => s.suggested_participant_id).filter(Boolean)
      const { data: resolvedParts } = resolvedPartIds.length > 0
        ? await supabase.from('participants').select('id, first_name, last_name').in('id', resolvedPartIds)
        : { data: [] }
      const rPartMap = Object.fromEntries((resolvedParts || []).map(p => [p.id, p]))
      setMyResolvedSuggestions(myResolved.map(s => ({
        ...s,
        suggested: rPartMap[s.suggested_participant_id] || null,
      })))
    } else {
      setMyResolvedSuggestions([])
    }
    await Promise.all([
      fetchActivities(scope.myParticipantId, scope.allUsers),
      fetchNuclei(scope.nucleusIds, scope.coreNucleusIds, isStale),
      fetchSessionCount(nucleusFilter),
      fetchParticipantCount(scope.allUsers, nucleusFilter, scope.myParticipantId, isStale),
      fetchRecentSessions(nucleusFilter),
      fetchActiveCampaigns(nucleusFilter),
    ])
    if (isStale()) return
    setLoading(false)
  }

  const fetchActivities = async (myParticipantId = null, allUsers = false) => {
    // RLS on recurring_activities returns only activities this user can see
    const { data } = await supabase
      .from('recurring_activities')
      .select('id, nickname, activity_type, locality, status, is_ysc, is_youth_devotional, current_book, current_grade, current_unit, current_section, current_lesson, photo_url, created_by, end_date, nucleus_id')
      .eq('status', 'active')
      .order('created_at', { ascending: false })
    setActivities(data || [])
  }

  const fetchNuclei = async (nucleusIds = null, coreNucleusIds = null, isStale = () => false) => {
    if (nucleusIds === null) {
      // Coordinator mode — show all nuclei
      const { data } = await supabase.from('nuclei').select('id, name, locality').eq('status', 'active')
      if (isStale()) return
      const sorted = (data || []).sort((a, b) => a.name.localeCompare(b.name))
      setNuclei(sorted)
      setCoreNuclei(sorted) // coordinators see all pills
      return
    }
    if (nucleusIds.length === 0) { if (isStale()) return; setNuclei([]); setCoreNuclei([]); return }
    const { data } = await supabase.from('nuclei')
      .select('id, name, locality').in('id', nucleusIds).eq('status', 'active')
    if (isStale()) return
    const sorted = (data || []).sort((a, b) => a.name.localeCompare(b.name))
    setNuclei(sorted)
    // Pills only show core team nuclei
    setCoreNuclei(coreNucleusIds
      ? sorted.filter(n => coreNucleusIds.includes(n.id))
      : sorted
    )
  }

  const fetchSessionCount = async (nFilter = null) => {
    const d30 = new Date(); d30.setDate(d30.getDate() - 30)
    const since = d30.toISOString().split('T')[0]
    // RLS returns only sessions this user can see
    let scQ = supabase.from('activity_sessions')
      .select('*', { count: 'exact', head: true })
      .gte('date', since)
      .is('deleted_at', null)
      .neq('activity_type', 'A009')
    if (nFilter) scQ = scQ.eq('nucleus_id', nFilter)
    const { count } = await scQ
    if (count !== null) setSessionCount(count)
  }

  const fetchParticipantCount = async (allUsers = false, nFilter = null, myParticipantId = null, isStale = () => false) => {
    const now = new Date()
    const d30 = new Date(); d30.setDate(now.getDate() - 30)
    const d90 = new Date(); d90.setDate(now.getDate() - 90)
    const since30 = d30.toISOString().split('T')[0]
    const since90 = d90.toISOString().split('T')[0]

    // RLS returns only sessions this user can see
    let sessQ = supabase.from('activity_sessions')
      .select('id, date, activity_type, facilitator_ids, assistant_ids, nucleus_id, visit_participant_ids')
      .gte('date', since90)
      .is('deleted_at', null)
      .neq('activity_type', 'A009')
    if (nFilter) sessQ = sessQ.eq('nucleus_id', nFilter)
    const { data: sessionRows } = await sessQ

    const sessions30 = (sessionRows || []).filter(s => s.date >= since30)
    const sessions90 = sessionRows || []

    // Build facilitator sets per window
    const facilitatorIds30 = new Set()
    const facilitatorIds90 = new Set()
    sessions30.forEach(s => (s.facilitator_ids || []).forEach(id => facilitatorIds30.add(String(id))))
    sessions90.forEach(s => (s.facilitator_ids || []).forEach(id => facilitatorIds90.add(String(id))))

    // Per-session facilitator map — to exclude facilitators from learner count
    const sessionFacMap = {}
    sessions30.forEach(s => { sessionFacMap[s.id] = new Set((s.facilitator_ids || []).map(String)) })

    // ── PARTICIPANTS: attended a non-outreach session within 30 days ────────────
    // OR were a named visited person (visit_participant_ids) in a home visit within 90 days
    const participantIds = new Set()

    // Source 1: session_attendance on core (non-visit, non-outreach) sessions
    // in last 30 days. Home visits (A006) and outreach (A007) are contact
    // touches, not participation, so both are excluded.
    const nonOutreachIds30 = sessions30
      .filter(s => !['A006', 'A007'].includes(s.activity_type))
      .map(s => s.id)

    if (nonOutreachIds30.length > 0) {
      const { data: attRows } = await supabase
        .from('session_attendance')
        .select('participant_id, session_id')
        .in('session_id', nonOutreachIds30)
        .eq('present', true)
      ;(attRows || []).forEach(a => {
        const id = String(a.participant_id)
        // Don't count them if they were the facilitator for THIS specific session
        const sessionFacs = sessionFacMap[a.session_id] || new Set()
        if (!sessionFacs.has(id)) participantIds.add(id)
      })
    }

    // (Home-visited people are contacts, not participants — see Source 1.
    // They become participants only by attending a core activity.)

    // Verify against participants table — exclude deleted
    let participantCount = 0
    if (participantIds.size > 0) {
      const { data: verified } = await supabase
        .from('participants').select('id')
        .in('id', [...participantIds])
        .is('deleted_at', null)
      participantCount = (verified || []).length
    }
    if (isStale()) return
    setParticipantCount(participantCount)

    // ── PROTAGONISTS: facilitated any session within 30 days ─────────────────
    // (90 days for home visits and outreach)
    // OR is a current active nucleus member (removed_at IS NULL, nucleus not archived)
    const protagonistIds = new Set()

    // Source 1: facilitators + assistants of non-visit/outreach sessions in last 30 days
    sessions30
      .filter(s => !['A006', 'A007'].includes(s.activity_type))
      .forEach(s => {
        ;(s.facilitator_ids || []).filter(Boolean).forEach(id => protagonistIds.add(String(id)))
        ;(s.assistant_ids   || []).filter(Boolean).forEach(id => protagonistIds.add(String(id)))
      })

    // Source 2: facilitators + assistants of home visits / outreach in last 90 days
    sessions90
      .filter(s => ['A006', 'A007'].includes(s.activity_type))
      .forEach(s => {
        ;(s.facilitator_ids || []).filter(Boolean).forEach(id => protagonistIds.add(String(id)))
        ;(s.assistant_ids   || []).filter(Boolean).forEach(id => protagonistIds.add(String(id)))
      })

    // Source 3: current active nucleus members (30-day grace on archive)
    // Scoped to active nucleus pill if set, otherwise to user's own nuclei
    let nucleusQ = supabase
      .from('nucleus_participants')
      .select('participant_id, nucleus_id, nuclei(status, archived_at)')
      .is('removed_at', null)
    if (nFilter) {
      // Nucleus pill active — only members of that specific nucleus
      nucleusQ = nucleusQ.eq('nucleus_id', nFilter)
    } else if (!allUsers) {
      const scope = await getNucleusScope(user.id, false)
      if (scope.nucleusIds?.length > 0) {
        nucleusQ = nucleusQ.in('nucleus_id', scope.nucleusIds)
      } else {
        nucleusQ = null
      }
    }
    const { data: activeMembers } = nucleusQ ? await nucleusQ : { data: [] }
    ;(activeMembers || []).forEach(m => {
      const nucleus = m.nuclei
      if (!nucleus) return
      // Exclude only if archived more than 30 days ago
      if (nucleus.status === 'archived' && nucleus.archived_at) {
        if (new Date(nucleus.archived_at) < d30) return
      }
      protagonistIds.add(String(m.participant_id))
    })

    // Verify against participants table — exclude deleted
    let protagonistCount = 0
    if (protagonistIds.size > 0) {
      const { data: verified } = await supabase
        .from('participants').select('id')
        .in('id', [...protagonistIds])
        .is('deleted_at', null)
      protagonistCount = (verified || []).length
    }
    setProtagonistCount(protagonistCount)
  }

  const fetchRecentSessions = async (nFilter = null) => {
    // RLS returns only sessions this user can see. We split into THREE
    // independently-limited queries — recent regular activities, standalone
    // one-off home visits, and meetings — so a burst in one category can't push
    // the others out of a shared window. (Previously a single limit(10) fed all
    // three buckets via JS filtering; a flurry of campaign one-off visits — which
    // land in NONE of the buckets — filled the window and starved "Recent
    // Sessions" entirely.)
    const SELECT = 'id, date, activity_type, nickname, notes, note_attachment_url, guidance_studied, present_count, logged_by, activity_id, photo_urls, created_at, is_one_off, nucleus_id, campaign_id, recurring_activities(nickname, nucleus_id)'
    const base = () => {
      let q = supabase.from('activity_sessions').select(SELECT)
        .is('deleted_at', null)
        .order('date', { ascending: false })
        .limit(10)
      if (nFilter) q = q.eq('nucleus_id', nFilter)
      return q
    }
    const mapRows = data => (data || []).map(s => ({ ...s, activityName: s.recurring_activities?.nickname || null }))

    const [regular, oneOffs, mtgs] = await Promise.all([
      // Regular activities: not a one-off (false or null) and not a meeting.
      base().not('is_one_off', 'is', true).neq('activity_type', 'A009'),
      // Standalone one-off home visits (not tied to a campaign).
      base().eq('is_one_off', true).eq('activity_type', 'A006').is('campaign_id', null),
      // Meetings.
      base().eq('activity_type', 'A009'),
    ])
    if (regular.error) console.error('fetchRecentSessions (regular) error:', regular.error)
    if (oneOffs.error) console.error('fetchRecentSessions (one-off) error:', oneOffs.error)
    if (mtgs.error) console.error('fetchRecentSessions (meetings) error:', mtgs.error)

    setRecentSessions(mapRows(regular.data))
    setOneOffEvents(mapRows(oneOffs.data))
    setMeetings(mapRows(mtgs.data).sort((a, b) => b.date.localeCompare(a.date)))
  }

  const fetchActiveCampaigns = async (nFilter = null) => {
    // RLS on campaigns is the single source of truth — mig 90 extended
    // the policy to include direct campaign-level facilitator/assistant
    // membership (campaigns.facilitator_ids / assistant_ids from mig 88),
    // and is_coordinator_mode() in the policy already opens visibility
    // for coords. We previously layered a client-side JS filter on top
    // of the RLS result to "mirror" the policy, but the mirror had to
    // re-read isCoordinator + nucleus scope from React state that lagged
    // the Context's async profile load. The result was that an in-flight
    // fetch could use the stale (pre-Context-resolve) values, strip a
    // coord-only-visible campaign back out, and produce the flashing /
    // disappearing symptom on Home. Trusting RLS removes that whole
    // class of bug: if the row came back from the DB, render it.
    let campQ = supabase.from('campaigns')
      .select('id, name, type, locality, nucleus_id, facilitator_ids, assistant_ids')
      .eq('status', 'active')
      .order('created_at', { ascending: false })
    if (nFilter) campQ = campQ.eq('nucleus_id', nFilter)
    const { data } = await campQ
    setActiveCampaigns(data || [])
  }

  const archiveActivity = async (id) => {
    const blocked = writeBlocked(await supabase.from('recurring_activities').update({ status: 'archived' }).eq('id', id).select('id'))
    if (blocked) {
      alert('Could not archive this activity: ' + blocked)
      return
    }
    fetchActivities(null, false)
  }

  const getType = (id) => ACTIVITY_TYPES[id] || { label: id, emoji: '📋', color: 'bg-stone-100 text-gray-800' }

  const getHour = () => today.getHours()
  const greeting = getHour() < 12 ? 'Good morning' : getHour() < 17 ? 'Good afternoon' : 'Good evening'

  // Build a short progress summary string for a session
  const sessionProgressSummary = (s) => {
    const parts = []
    if (s.book) parts.push(`Book ${s.book}`)
    if (s.grade) parts.push(`Grade ${s.grade}`)
    if (s.units_completed && s.units_completed.length > 0) parts.push(`Unit${s.units_completed.length > 1 ? 's' : ''} ${s.units_completed.join(', ')}`)
    if (s.sections_completed && s.sections_completed.length > 0) parts.push(`Sec ${s.sections_completed.join(', ')}`)
    if (s.lesson) parts.push(`Lesson ${s.lesson}`)
    return parts.join(' · ')
  }

  const Q = v => `"${(v??'').toString().replace(/"/g,'""')}"`
  const toRow = obj => Object.values(obj).map(Q).join(',')
  const dl = (content, filename) => {
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(new Blob([content], {type:'text/csv'})), download: filename })
    a.click(); URL.revokeObjectURL(a.href)
  }
  const todayStr = () => new Date().toISOString().slice(0,10)

  const handleApproveMerge = async (suggestion) => {
    setResolvingSuggestion(true)
    try {
      const newId = suggestion.new_participant?.id
      const suggestedId = suggestion.suggested?.id
      const profileId = suggestion.profile?.id   // null for scan-generated suggestions

      // Safety (a): both records must be referenced on the suggestion. Hard-deletes
      // null out new_participant_id via migration 08's FK ON DELETE SET NULL.
      if (!newId || !suggestedId) {
        alert('Merge cancelled: one or both records are missing from the suggestion. Please refresh.')
        setResolvingSuggestion(false)
        return
      }

      // Direction discriminator — which side to keep depends on how the suggestion
      // was generated. The UI labels "Record to drop (merge from)" on new_participant
      // and "Record to keep (merge into)" on suggested for field-worker-add and scan
      // paths. Signup-fuzzy inverts this because profile.participant_id is auto-linked
      // to the newly-created participant (per Blueprint §3.1 — auto_link_or_create_
      // participant runs BEFORE INSERT on profiles, then check_fuzzy_match_after_signup
      // runs AFTER INSERT). We use that invariant to distinguish the two paths.
      let keepId, dropId
      let isSignupFuzzy = false
      if (profileId) {
        const { data: profileCheck } = await supabase
          .from('profiles').select('participant_id').eq('id', profileId).single()
        if (profileCheck?.participant_id === newId) {
          // Signup-fuzzy: profile is auto-linked to new_participant. Keep it.
          keepId = newId
          dropId = suggestedId
          isSignupFuzzy = true
        } else {
          // Field-worker-add: profile_id is the adder's auth (their own participant,
          // unrelated to either merge side). Follow UI labels.
          keepId = suggestedId
          dropId = newId
        }
      } else {
        // Scan path: profile_id null. Follow UI labels.
        keepId = suggestedId
        dropId = newId
      }

      // Safety (b): guard against a malformed suggestion where both sides collapsed
      // to the same participant. Rare, but merge logic below assumes distinct ids.
      if (keepId === dropId) {
        alert('Merge cancelled: both records refer to the same participant. Please refresh.')
        setResolvingSuggestion(false)
        return
      }

      // ── conflict detection (Phase C commit 2) ─────────────────────────────────
      // If both records have non-empty, normalized-different values on any
      // shared column, surface the ConflictResolutionModal first. Otherwise
      // fall through to the merge with no resolutions.
      const detected = await detectMergeConflicts(supabase, keepId, dropId)
      let { conflicts } = detected
      const { keep, drop } = detected

      // Signup-fuzzy path: the signing-up user typed their own name. That
      // wins automatically — coord shouldn't be asked to pick. Filter
      // first_name / last_name out of the conflict modal. merge_participant_
      // progress's COALESCE then naturally preserves keep's non-empty
      // name values. Other conflicts (phone, email, etc.) still surface
      // for coord adjudication. May 15 2026 Ben/Benjamin Fanning incident.
      if (isSignupFuzzy && conflicts.length > 0) {
        conflicts = conflicts.filter(c =>
          c.field !== 'first_name' && c.field !== 'last_name'
        )
      }

      if (conflicts.length > 0) {
        setPendingMerge({ keepId, dropId, keep, drop, conflicts, suggestion })
        setResolvingSuggestion(false)
        return
      }
      await finalizeMerge({ keepId, dropId, suggestion, resolutions: null })
    } catch (e) {
      alert('Merge failed: ' + e.message)
      setResolvingSuggestion(false)
    }
  }

  // Shared post-resolution merge step. Called either directly (no conflicts)
  // or via the ConflictResolutionModal's onResolve handler.
  const finalizeMerge = async ({ keepId, dropId, suggestion, resolutions }) => {
    setResolvingSuggestion(true)
    try {
      const { ok, error: mergeErr } = await mergeTwoParticipants(keepId, dropId, resolutions)
      if (!ok) throw new Error(mergeErr || 'Merge failed')

      // Store the SURVIVING (kept) participant's name in duplicate_name
      // so the home-feed banner can render "Your entry has been merged
      // into [keep_name]'s profile." — same convention used by the
      // synthesized rows from manual + auto-merges (Phase D commit 5).
      // Fall back to suggestion.suggested if a fresh row read fails.
      const { data: keepRow } = await supabase
        .from('participants')
        .select('first_name, last_name')
        .eq('id', keepId)
        .maybeSingle()
      const mergeDupName = (
        `${keepRow?.first_name || suggestion.suggested?.first_name || ''} ` +
        `${keepRow?.last_name  || suggestion.suggested?.last_name  || ''}`
      ).trim()
      await supabase.from('profile_match_suggestions').update({
        status: 'approved',
        duplicate_name: mergeDupName,
        resolved_at: new Date().toISOString()
      }).eq('id', suggestion.id)

      setMatchSuggestions(s => s.filter(x => x.id !== suggestion.id))
      setPendingMerge(null)
    } catch (e) {
      alert('Merge failed: ' + e.message)
    }
    setResolvingSuggestion(false)
  }

  const handleRejectMerge = async (suggestion) => {
    await supabase.from('profile_match_suggestions').update({ status: 'rejected' }).eq('id', suggestion.id)
    setMatchSuggestions(s => s.filter(x => x.id !== suggestion.id))
  }

  // Currently unused — Delete button removed from UI in commit 3 of
  // merge-relationship-preservation. Function retained in case
  // hard-delete UI is reintroduced later.
  const handleDeleteDuplicate = async (suggestion) => {
    const dupName = `${suggestion.new_participant?.first_name || ''} ${suggestion.new_participant?.last_name || ''}`.trim() || 'the duplicate'
    const keptName = `${suggestion.suggested?.first_name || ''} ${suggestion.suggested?.last_name || ''}`.trim() || 'the existing record'
    const msg =
      `Keep ${keptName} and remove the duplicate ${dupName}?\n\n` +
      `Any sessions, activities, or memberships on the duplicate will be transferred to ${keptName}.\n\n` +
      `This cannot be undone.`
    if (!window.confirm(msg)) return
    setResolvingSuggestion(true)
    try {
      const dupId = suggestion.new_participant.id
      const keepId = suggestion.suggested.id
      // Ensure the real person is visible to any nucleus the duplicate was connected to
      const { data: dupNuclei } = await supabase
        .from('participant_nucleus_connections')
        .select('nucleus_id')
        .eq('participant_id', dupId)
      if (dupNuclei?.length) {
        const { data: existingPnc } = await supabase
          .from('participant_nucleus_connections')
          .select('nucleus_id')
          .eq('participant_id', keepId)
        const existingNucIds = new Set((existingPnc || []).map(r => r.nucleus_id))
        const missing = dupNuclei.filter(r => !existingNucIds.has(r.nucleus_id))
        if (missing.length > 0) {
          await supabase.from('participant_nucleus_connections').insert(
            missing.map(r => ({ participant_id: keepId, nucleus_id: r.nucleus_id }))
          )
        }
      }
      // Also connect real person to nuclei of whoever added
      // the duplicate — covers the case where the duplicate
      // was added via Add Person without attending any session
      const adderAuthId = suggestion.new_participant?.added_by
      let adderProfile = null
      if (adderAuthId) {
        const { data: ap } = await supabase
          .from('profiles')
          .select('participant_id')
          .eq('id', adderAuthId)
          .single()
        adderProfile = ap
        if (adderProfile?.participant_id) {
          const { data: adderNuclei } = await supabase
            .from('nucleus_participants')
            .select('nucleus_id')
            .eq('participant_id', adderProfile.participant_id)
            .is('removed_at', null)
          if (adderNuclei?.length) {
            const { data: existingPnc2 } = await supabase
              .from('participant_nucleus_connections')
              .select('nucleus_id')
              .eq('participant_id', keepId)
            const existingNucIds2 = new Set(
              (existingPnc2 || []).map(r => r.nucleus_id)
            )
            const missing2 = adderNuclei.filter(
              r => !existingNucIds2.has(r.nucleus_id)
            )
            if (missing2.length > 0) {
              await supabase
                .from('participant_nucleus_connections')
                .insert(missing2.map(r => ({
                  participant_id: keepId,
                  nucleus_id: r.nucleus_id
                })))
            }
          }
        }
      }
      // Fallback: check the adder's own PNC rows (not just nucleus_participants)
      if (adderProfile?.participant_id) {
        const { data: adderPnc } = await supabase
          .from('participant_nucleus_connections')
          .select('nucleus_id')
          .eq('participant_id', adderProfile.participant_id)
        if (adderPnc?.length) {
          const { data: existingPnc3 } = await supabase
            .from('participant_nucleus_connections')
            .select('nucleus_id')
            .eq('participant_id', keepId)
          const existingNucIds3 = new Set(
            (existingPnc3 || []).map(r => r.nucleus_id)
          )
          const missing3 = adderPnc.filter(
            r => !existingNucIds3.has(r.nucleus_id)
          )
          if (missing3.length > 0) {
            await supabase
              .from('participant_nucleus_connections')
              .insert(missing3.map(r => ({
                participant_id: keepId,
                nucleus_id: r.nucleus_id
              })))
          }
        }
      }
      // Unlink the profile from the duplicate participant before deleting
      if (suggestion.profile_id) {
        await supabase.from('profiles')
          .update({ participant_id: null })
          .eq('id', suggestion.profile_id)
          .eq('participant_id', dupId)
      }
      // Update suggestion status FIRST before deleting the
      // participant — the participant delete may cascade-delete
      // the suggestion row
      await supabase.from('profile_match_suggestions')
        .update({
          status: 'rejected',
          duplicate_name: dupName,
          resolved_at: new Date().toISOString()
        })
        .eq('id', suggestion.id)

      // Re-point child rows from the duplicate to the kept record BEFORE
      // deleting. Pattern mirrors handleApproveMerge (lines 405-431): for
      // each table, fetch keepId's existing rows to know what would
      // conflict on the unique key, delete conflicting dupId rows, then
      // update the remaining dupId rows to point at keepId.
      //
      // nucleus_participants — unique: (nucleus_id, participant_id)
      const { data: keepNuclei } = await supabase
        .from('nucleus_participants').select('nucleus_id').eq('participant_id', keepId)
      const keepNucleusIds = (keepNuclei || []).map(r => r.nucleus_id)
      if (keepNucleusIds.length > 0) {
        await supabase.from('nucleus_participants')
          .delete().eq('participant_id', dupId).in('nucleus_id', keepNucleusIds)
      }
      await supabase.from('nucleus_participants')
        .update({ participant_id: keepId }).eq('participant_id', dupId)

      // activity_participants — unique: (activity_id, participant_id)
      const { data: keepActs } = await supabase
        .from('activity_participants').select('activity_id').eq('participant_id', keepId)
      const keepActivityIds = (keepActs || []).map(r => r.activity_id)
      if (keepActivityIds.length > 0) {
        await supabase.from('activity_participants')
          .delete().eq('participant_id', dupId).in('activity_id', keepActivityIds)
      }
      await supabase.from('activity_participants')
        .update({ participant_id: keepId }).eq('participant_id', dupId)

      // session_attendance — no DB-level unique on (session_id, participant_id)
      // but semantically only one attendance row per (session, participant)
      // makes sense. Dedupe-first pattern here is safer than handleApproveMerge's
      // unconditional update at line 402-403. TODO: bring handleApproveMerge
      // into alignment with this pattern in a follow-up.
      const { data: keepSessions } = await supabase
        .from('session_attendance').select('session_id').eq('participant_id', keepId)
      const keepSessionIds = (keepSessions || []).map(r => r.session_id)
      if (keepSessionIds.length > 0) {
        await supabase.from('session_attendance')
          .delete().eq('participant_id', dupId).in('session_id', keepSessionIds)
      }
      await supabase.from('session_attendance')
        .update({ participant_id: keepId }).eq('participant_id', dupId)

      // Remaining cleanups: no semantic value to transfer, safe to delete.
      // PNC rows for the duplicate were already copied to keepId earlier;
      // these dupId PNC rows are now redundant.
      await supabase.from('participant_nucleus_connections').delete().eq('participant_id', dupId)
      await supabase.from('participant_hidden').delete().eq('participant_id', dupId)
      // Preserve added_by history before delete (migration 27 RPC).
      await supabase.rpc('merge_added_by_history', { p_drop_id: dupId, p_keep_id: keepId })
      await supabase.from('participants').delete().eq('id', dupId)
      setMatchSuggestions(s => s.filter(x => x.id !== suggestion.id))
    } catch (e) {
      alert('Delete failed: ' + e.message)
    }
    setResolvingSuggestion(false)
  }

  const handleDismissAutoLink = async () => {
    setDismissedAutoLink(true)
    await supabase.from('profiles').update({ auto_linked: false }).eq('id', user.id)
  }

  const handleDismissNotification = async (suggestionId) => {
    const { data, error } = await supabase.from('profile_match_suggestions')
      .update({ status: 'dismissed' })
      .eq('id', suggestionId)
      .select()  // forces RETURNING so we can detect silent RLS rejections
    if (error) {
      alert('Could not dismiss: ' + error.message)
      return
    }
    if (!data?.length) {
      alert('Could not dismiss — no matching row or insufficient permission. Please refresh and try again.')
      return
    }
    setMyResolvedSuggestions(s => s.filter(x => x.id !== suggestionId))
  }

  const handleScanDuplicates = async () => {
    setScanLoading(true)
    try {
      const { data: pairs } = await supabase
        .rpc('scan_for_duplicates', { threshold: 0.56 })

      if (!pairs?.length) {
        alert('No new duplicates found.')
        setScanLoading(false)
        return
      }

      const existingPairs = new Set(
        matchSuggestions.map(s =>
          [s.suggested_participant_id, s.new_participant_id]
            .sort().join('-')
        )
      )

      const newPairs = pairs.filter(p => {
        const key = [p.participant_a_id, p.participant_b_id]
          .sort().join('-')
        return !existingPairs.has(key)
      })

      if (!newPairs.length) {
        alert('No new duplicates found beyond existing suggestions.')
        setScanLoading(false)
        return
      }

      await supabase.from('profile_match_suggestions').insert(
        newPairs.map(p => ({
          profile_id: null,
          suggested_participant_id: p.participant_a_id,
          new_participant_id: p.participant_b_id,
          similarity_score: p.similarity_score,
          status: 'pending',
        }))
      )

      await fetchAll()
      alert(`Found ${newPairs.length} new potential duplicate(s).`)
    } catch (e) {
      alert('Scan failed: ' + e.message)
    }
    setScanLoading(false)
  }

  const handleDownloadCSV = async () => {
    setExporting(true)
    try {
      // Active ongoing activities + recent sessions (last 30 days)
      const since = new Date(); since.setDate(since.getDate() - 30)
      const sinceStr = since.toISOString().split('T')[0]
      const [sessR, recurR, partsR, profR] = await Promise.all([
        supabase.from('activity_sessions').select('id,date,activity_type,nickname,present_count,notes,facilitator_ids,facilitator_name,logged_by,nucleus_id,is_one_off').is('deleted_at',null).gte('date',sinceStr).order('date',{ascending:false}),
        supabase.from('recurring_activities').select('id,nickname,activity_type,nucleus_id,facilitator_ids,status').eq('status','active').is('deleted_at',null),
        supabase.from('participants').select('id,first_name,last_name').is('deleted_at',null),
        supabase.from('profiles').select('id,full_name'),
      ])
      const profMap = {}; (profR.data||[]).forEach(p=>{profMap[p.id]=p.full_name})
      const partMap = {}; (partsR.data||[]).forEach(p=>{partMap[p.id]=`${p.first_name} ${p.last_name||''}`.trim()})

      // Ongoing activities sheet
      const actHdr = "Activity Name,Type,Status,Nucleus"
      const actRows = (recurR.data||[]).map(a => toRow({
        name: a.nickname||'',
        type: ACTIVITY_TYPES[a.activity_type]?.label||a.activity_type,
        status: a.status,
        nucleus: a.nucleus_id||'',
      }))

      // Recent sessions sheet
      const sessHdr = "Date,Activity,Type,Field Worker(s),Attendance,Notes"
      const sessRows = (sessR.data||[]).map(s => {
        const facNames = (s.facilitator_ids||[]).map(id=>partMap[id]||profMap[id]||'').filter(Boolean).join('; ')||s.facilitator_name||profMap[s.logged_by]||''
        return toRow({date:s.date,name:s.nickname||(ACTIVITY_TYPES[s.activity_type]?.label||s.activity_type),type:ACTIVITY_TYPES[s.activity_type]?.label||s.activity_type,fw:facNames,att:s.present_count||'',notes:s.notes||''})
      })

      const content = ['ONGOING ACTIVITIES',actHdr,...actRows,'\n','RECENT SESSIONS (LAST 30 DAYS)',sessHdr,...sessRows].join('\n')
      dl(content, `home-snapshot-${todayStr()}.csv`)
    } catch(e) { alert('Export failed: '+e.message) }
    setExporting(false)
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-36">

      {/* Header */}
      <div className="bg-teal-700 text-white px-4 pt-6 pb-8">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <span className="text-2xl">📓</span>
            <span className="font-bold text-lg">My Notebook</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/search')} aria-label="Search all visits"
              className="text-teal-200 text-sm px-2.5 py-1.5 rounded-lg border border-teal-500">
              🔍
            </button>
            <button onClick={handleDownloadCSV} disabled={exporting}
              className="text-teal-200 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-teal-500 disabled:opacity-50">
              {exporting ? '…' : '⬇ CSV'}
            </button>
            <button onClick={() => signOut(user?.id)} className="text-teal-200 text-sm">Sign out</button>
          </div>
        </div>
        <p className="text-teal-300 text-xs">{dateStr}</p>
      </div>

      <div className="px-4 max-w-lg mx-auto -mt-4">

        {/* Finish-your-profile prompt — signup didn't capture a name (typically email-signup without OAuth metadata).
            Require `profile` to be loaded (not null) so a still-loading or flaky-network-failed profile fetch
            doesn't spuriously nag — the prompt only shows when the profile resolved and full_name is genuinely empty. */}
        {user && profile && !profile.full_name && (
          <div className="bg-teal-50 border border-teal-200 rounded-2xl px-4 py-3 mb-4">
            <div className="flex items-start gap-3">
              <span className="text-xl">👤</span>
              <div className="flex-1">
                <p className="text-teal-800 font-semibold text-sm">Finish your profile</p>
                <p className="text-teal-600 text-xs mt-0.5 leading-relaxed">Add your name so teammates can identify you.</p>
              </div>
              <button onClick={() => navigate('/account')}
                className="bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold px-3 py-2 rounded-xl flex-shrink-0 transition-colors">
                Add name
              </button>
            </div>
          </div>
        )}

        {/* Match suggestions banner — visible only when coord mode is active */}
        {coordinatorMode && matchSuggestions.length > 0 && (
          <div className="bg-amber-500 rounded-2xl shadow-sm px-4 py-3 mb-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <p className="text-white font-bold text-sm">
                  {matchSuggestions.length} possible duplicate {matchSuggestions.length === 1 ? 'profile' : 'profiles'} need review
                </p>
                <p className="text-amber-100 text-xs">A new user may already exist in your community</p>
              </div>
            </div>
            <button onClick={() => setShowMatchModal(true)}
              className="bg-white text-amber-600 font-bold text-xs px-3 py-2 rounded-xl flex-shrink-0 hover:bg-amber-50 transition-colors">
              Review
            </button>
          </div>
        )}

        {/* User's own pending match notice */}
        {myPendingSuggestion && !profile?.coordinator_mode_active && (
          <div className="bg-teal-50 border border-teal-200 rounded-2xl px-4 py-3 mb-4">
            <div className="flex items-start gap-3">
              <span className="text-xl">🔗</span>
              <div>
                <p className="text-teal-800 font-semibold text-sm">Your account may match an existing profile</p>
                <p className="text-teal-600 text-xs mt-0.5 leading-relaxed">
                  A coordinator has been notified and will review your account. If you think you already have a profile in the community, contact your coordinator to confirm.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Auto-linked notice — shown when account was silently linked via email match */}
        {profile?.auto_linked && !dismissedAutoLink && (
          <div className="bg-teal-50 border border-teal-200 rounded-2xl px-4 py-3 mb-4">
            <div className="flex items-start gap-3">
              <span className="text-xl">✅</span>
              <div className="flex-1">
                <p className="text-teal-800 font-semibold text-sm">Your account has been linked to your existing profile</p>
                <p className="text-teal-600 text-xs mt-0.5 leading-relaxed">
                  We found an existing record in the community matching your email and automatically connected it to your account.
                </p>
              </div>
              <button onClick={handleDismissAutoLink} className="text-teal-400 hover:text-teal-600 text-xl leading-none flex-shrink-0">×</button>
            </div>
          </div>
        )}

        {/* Duplicate resolved notifications — field worker only.
            For status='approved' (merges), duplicate_name stores the
            kept participant's name (Phase D convention). For
            status='rejected' (delete-duplicate path), duplicate_name
            stores the dropped name and we keep the legacy wording. */}
        {myResolvedSuggestions.map(s => {
          const realName = `${s.suggested?.first_name || ''} ${s.suggested?.last_name || ''}`.trim()
          return (
            <div key={s.id} className="bg-teal-50 border border-teal-200 rounded-2xl px-4 py-3 mb-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-teal-800">Duplicate resolved</p>
                  <p className="text-xs text-teal-700 mt-0.5">
                    {s.status === 'approved'
                      ? <>Your entry has been merged into {s.duplicate_name || realName}'s profile on your People page.</>
                      : <>"{s.duplicate_name}" was identified as a duplicate and removed. You can find them as {realName} on your People page.</>
                    }
                  </p>
                </div>
                <button onClick={() => handleDismissNotification(s.id)}
                  className="text-teal-400 hover:text-teal-600 text-xl leading-none flex-shrink-0">×</button>
              </div>
            </div>
          )
        })}

        {/* Match review modal */}
        {showMatchModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50">
            <div className="bg-white rounded-t-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
              <div className="px-4 py-4 border-b border-stone-100 flex items-center justify-between">
                <h2 className="text-base font-bold text-gray-800">⚠️ Possible Duplicate Profiles</h2>
                <button onClick={() => setShowMatchModal(false)} className="text-stone-400 text-2xl leading-none">×</button>
              </div>
              <div className="p-4 space-y-4">
                <p className="text-xs text-stone-400 leading-relaxed">
                  Review potential duplicates and choose to merge the records or keep them separate.
                </p>
                {matchSuggestions.map(s => {
                  const isSignupSuggestion = s.profile &&
                    s.new_participant &&
                    s.profile.full_name?.toLowerCase().includes(
                      s.new_participant.first_name?.toLowerCase()
                    )
                  const newName = `${s.new_participant?.first_name || ''} ${s.new_participant?.last_name || ''}`.trim()
                  const sugName = `${s.suggested?.first_name || ''} ${s.suggested?.last_name || ''}`.trim()
                  return (
                  <div key={s.id} className="bg-stone-50 rounded-2xl p-4 space-y-3">
                    {isSignupSuggestion ? (
                      <>
                        <div>
                          <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-1">New User</p>
                          <p className="text-sm font-semibold text-gray-800">{s.profile?.full_name || 'Unknown'}</p>
                          <p className="text-xs text-stone-500">{s.new_participant?.email || 'No email'}</p>
                          {s.created_at && (
                            <p className="text-xs text-stone-400 mt-0.5">Signed up {new Date(s.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                          )}
                          <p className={`text-xs font-medium mt-1 ${s.newHasHistory ? 'text-amber-600' : 'text-teal-600'}`}>
                            {s.newHasHistory ? '⚠️ Has connections — use Merge, not Delete' : '✓ No connections — safe to delete'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-px bg-stone-200" />
                          <span className="text-xs text-amber-500 font-semibold">{Math.round((s.similarity_score || 0) * 100)}% match</span>
                          <div className="flex-1 h-px bg-stone-200" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-1">Existing Person</p>
                          <p className="text-sm font-semibold text-gray-800">{sugName}</p>
                          <p className="text-xs text-stone-500">{s.suggested?.email || 'No email on record'}</p>
                          <p className={`text-xs font-medium mt-1 ${s.suggestedHasHistory ? 'text-teal-600' : 'text-stone-400'}`}>
                            {s.suggestedHasHistory ? '⭐ Has connections' : '○ No connections'}
                          </p>
                        </div>
                        {/* Top-3 candidate picker (Phase D commit 6). Hidden after Swap
                            since the candidates were computed for the original new side. */}
                        {!s._isSwapped && s.candidates?.filter(c => c.id !== s.suggested?.id).length > 0 && (
                          <div className="mt-1">
                            <p className="text-xs text-stone-400 uppercase tracking-wide mb-1">Other matches</p>
                            <div className="space-y-1">
                              {s.candidates.filter(c => c.id !== s.suggested?.id).map(c => (
                                <button key={c.id}
                                  onClick={() => setMatchSuggestions(prev => prev.map(x =>
                                    x.id === s.id
                                      ? { ...x, _pickedId: c.id, suggested: c, suggested_participant_id: c.id }
                                      : x
                                  ))}
                                  className="w-full text-left text-xs px-3 py-2 rounded-lg bg-white hover:bg-stone-100 border border-stone-200">
                                  <span className="font-medium text-gray-800">{c.first_name} {c.last_name}</span>
                                  {c.approximate_age != null && <span className="text-stone-500"> · age {c.approximate_age}</span>}
                                  {c.locality && <span className="text-stone-500"> · {c.locality}</span>}
                                  <span className="text-amber-500 ml-2">{Math.round((c.sim || 0) * 100)}%</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-1">Record to drop (merge from)</p>
                            <p className="text-sm font-semibold text-gray-800">{newName || 'Unknown'}</p>
                            <p className="text-xs text-stone-500">{s.new_participant?.email || 'No email'}</p>
                            <p className={`text-xs font-medium mt-1 ${s.newHasHistory ? 'text-amber-600' : 'text-teal-600'}`}>
                              {s.newHasHistory ? '⚠️ Has connections — use Merge, not Delete' : '✓ No connections — safe to delete'}
                            </p>
                            {s.profile && (
                              <p className="text-xs text-stone-400 mt-0.5">Added by {s.profile.full_name || 'unknown'}</p>
                            )}
                          </div>
                          <button
                            onClick={() => setMatchSuggestions(prev => prev.map(x =>
                              x.id === s.id
                                ? { ...x,
                                    suggested_participant_id: x.new_participant_id,
                                    new_participant_id: x.suggested_participant_id,
                                    suggested: x.new_participant,
                                    new_participant: x.suggested,
                                    newHasHistory: x.suggestedHasHistory,
                                    suggestedHasHistory: x.newHasHistory,
                                    _isSwapped: !x._isSwapped,
                                  }
                                : x
                            ))}
                            className="text-xs text-teal-600 font-semibold bg-teal-50 hover:bg-teal-100 px-2 py-1 rounded-lg flex-shrink-0 border border-teal-200">
                            ↕ Swap
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-px bg-stone-200" />
                          <span className="text-xs text-amber-500 font-semibold">{Math.round((s.similarity_score || 0) * 100)}% match</span>
                          <div className="flex-1 h-px bg-stone-200" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-1">Record to keep (merge into)</p>
                          <p className="text-sm font-semibold text-gray-800">{sugName}</p>
                          <p className="text-xs text-stone-500">{s.suggested?.email || 'No email on record'}</p>
                          <p className={`text-xs font-medium mt-1 ${s.suggestedHasHistory ? 'text-teal-600' : 'text-stone-400'}`}>
                            {s.suggestedHasHistory ? '⭐ Has connections' : '○ No connections'}
                          </p>
                        </div>
                        {/* Top-3 candidate picker (Phase D commit 6). Hidden after Swap
                            since the candidates were computed for the original new side. */}
                        {!s._isSwapped && s.candidates?.filter(c => c.id !== s.suggested?.id).length > 0 && (
                          <div className="mt-1">
                            <p className="text-xs text-stone-400 uppercase tracking-wide mb-1">Other matches</p>
                            <div className="space-y-1">
                              {s.candidates.filter(c => c.id !== s.suggested?.id).map(c => (
                                <button key={c.id}
                                  onClick={() => setMatchSuggestions(prev => prev.map(x =>
                                    x.id === s.id
                                      ? { ...x, _pickedId: c.id, suggested: c, suggested_participant_id: c.id }
                                      : x
                                  ))}
                                  className="w-full text-left text-xs px-3 py-2 rounded-lg bg-white hover:bg-stone-100 border border-stone-200">
                                  <span className="font-medium text-gray-800">{c.first_name} {c.last_name}</span>
                                  {c.approximate_age != null && <span className="text-stone-500"> · age {c.approximate_age}</span>}
                                  {c.locality && <span className="text-stone-500"> · {c.locality}</span>}
                                  <span className="text-amber-500 ml-2">{Math.round((c.sim || 0) * 100)}%</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {/* Actions */}
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => handleApproveMerge(s)}
                        disabled={resolvingSuggestion}
                        className="flex-1 bg-teal-700 hover:bg-teal-800 disabled:opacity-50 text-white font-bold py-2.5 rounded-xl text-sm transition-colors">
                        {resolvingSuggestion ? 'Merging...' : '✓ Merge Records'}
                      </button>
                      <button
                        onClick={() => handleRejectMerge(s)}
                        disabled={resolvingSuggestion}
                        className="flex-1 bg-stone-100 hover:bg-stone-200 disabled:opacity-50 text-stone-600 font-bold py-2.5 rounded-xl text-sm transition-colors">
                        Keep Separate
                      </button>
                    </div>
                  </div>
                  )
                })}
                {matchSuggestions.length === 0 && (
                  <p className="text-sm text-stone-400 text-center py-4">All resolved ✓</p>
                )}
                {coordinatorMode && (
                  <button
                    onClick={handleScanDuplicates}
                    disabled={scanLoading}
                    className="w-full border-2 border-teal-300 hover:bg-teal-50 disabled:opacity-50 text-teal-700 font-bold py-2.5 rounded-xl text-sm transition-colors">
                    {scanLoading ? 'Scanning...' : '🔍 Scan for Duplicates'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Greeting card */}
        <div className="bg-white rounded-2xl shadow-sm px-5 py-4 mb-4">
          <h2 className="text-2xl font-bold text-gray-800">
            {greeting}, {profile?.full_name?.split(' ')[0] || 'friend'} ✨
          </h2>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-white rounded-2xl p-3 text-center shadow-sm">
            <div className="text-2xl font-bold text-teal-600">{sessionCount}</div>
            <div className="text-xs text-stone-400 mt-1 leading-tight">Sessions (30 days)</div>
          </div>
          <div className="bg-white rounded-2xl p-3 text-center shadow-sm">
            <div className="text-2xl font-bold text-teal-600">{participantCount}</div>
            <div className="text-xs text-stone-400 mt-1 leading-tight">Participants (30 days)</div>
          </div>
          <div className="bg-white rounded-2xl p-3 text-center shadow-sm">
            <div className="text-2xl font-bold text-teal-600">{protagonistCount}</div>
            <div className="text-xs text-stone-400 mt-1 leading-tight">Protagonists (30 days)</div>
          </div>
        </div>

        {/* My Nuclei — core team nuclei only */}
        {coreNuclei.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold text-stone-400 uppercase tracking-widest">My Nuclei</h2>
            </div>
            <div className="space-y-2 mb-4">
              {coreNuclei.map(nucleus => (
                <button key={nucleus.id} onClick={() => navigate('/nucleus/' + nucleus.id)}
                  className="w-full bg-white rounded-2xl px-4 py-3 shadow-sm flex items-center justify-between text-left hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">🌿</span>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">{nucleus.name}</p>
                      {nucleus.locality && <p className="text-xs text-stone-400">{nucleus.locality}</p>}
                    </div>
                  </div>
                  <span className="text-stone-300 text-lg">›</span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* Nucleus filter — only for core team nuclei */}
        {coreNuclei.length > 0 && (
          <div className="flex gap-2 mb-3 overflow-x-auto pb-1 scrollbar-hide">
            <button onClick={() => setNucleusFilter(null)}
              className={`flex-shrink-0 text-xs px-3 py-1.5 rounded-full font-semibold border transition-colors ${!nucleusFilter ? 'bg-teal-700 text-white border-teal-700' : 'border-stone-200 text-stone-600'}`}>
              All
            </button>
            {coreNuclei.map(n => (
              <button key={n.id} onClick={() => setNucleusFilter(n.id)}
                className={`flex-shrink-0 text-xs px-3 py-1.5 rounded-full font-semibold border transition-colors ${nucleusFilter === n.id ? 'bg-teal-700 text-white border-teal-700' : 'border-stone-200 text-stone-600'}`}>
                {n.name}
              </button>
            ))}
          </div>
        )}

        {/* Ongoing Activities */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-semibold text-stone-400 uppercase tracking-widest">Ongoing Activities</h2>
          <button onClick={() => navigate('/activities')} className="text-xs text-teal-600 font-semibold">+ New</button>
        </div>

        {/* ── Active Campaign Cards (inside Ongoing Activities) ──
            Loading gate: don't paint campaign cards while fetchAll is
            mid-flight. Without this, a fetch that's about to set
            activeCampaigns to a different shape (e.g. when coordMode
            ticks from the useState default to the Context-resolved DB
            value, re-firing the useEffect) would paint the
            previous-fetch's result for one frame, producing the
            "flashing in and out" symptom. The activities list below
            already gates on !loading; campaigns get the same treatment. */}
        {!loading && activeCampaigns.filter(camp => !nucleusFilter || !camp.nucleus_id || camp.nucleus_id === nucleusFilter).length > 0 && (
          <div className="space-y-2 mb-3">
            {activeCampaigns.filter(camp => !nucleusFilter || !camp.nucleus_id || camp.nucleus_id === nucleusFilter).map(campaign => (
              <div key={campaign.id} className="bg-amber-500 rounded-2xl px-4 py-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <button className="min-w-0 flex-1 text-left" onClick={() => navigate(`/campaigns/${campaign.id}`)}>
                    <p className="text-xs font-semibold text-amber-100 uppercase tracking-widest mb-0.5">
                      Active Campaign
                    </p>
                    <p className="font-bold text-white truncate">{campaign.name}</p>
                    {campaign.locality && (
                      <p className="text-amber-100 text-xs mt-0.5">📍 {campaign.locality} · Tap to view</p>
                    )}
                  </button>
                  <div className="flex gap-2 flex-shrink-0 ml-3">
                    <button
                      onClick={() => navigate('/activities/A006', { state: { campaignId: campaign.id } })}
                      className="bg-white text-amber-600 text-xs font-bold px-3 py-2 rounded-xl">
                      🏠 Visit
                    </button>
                    <button
                      onClick={() => navigate('/activities/A007', { state: { campaignId: campaign.id } })}
                      className="bg-amber-600 text-white text-xs font-bold px-3 py-2 rounded-xl border border-amber-400">
                      🤝 Outreach
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center text-teal-700 py-4">Loading...</div>
        ) : activities.length === 0 && activeCampaigns.filter(camp => !nucleusFilter || !camp.nucleus_id || camp.nucleus_id === nucleusFilter).length === 0 ? (
          <div className="bg-white rounded-xl p-6 text-center shadow-sm mb-4">
            <p className="text-stone-500 text-sm">No activities yet.</p>
            <p className="text-stone-400 text-xs mt-1">Tap + New to create your first activity</p>
          </div>
        ) : (
          <div className="space-y-3 mb-4">
            {(showAllActivities
              ? activities.filter(a => !nucleusFilter || a.nucleus_id === nucleusFilter)
              : activities.filter(a => !nucleusFilter || a.nucleus_id === nucleusFilter).slice(0, 5)
            ).map((activity) => {
              const type = getType(activity.activity_type)
              return (
                <div key={activity.id}
                  className="w-full bg-white rounded-2xl shadow-sm overflow-hidden flex items-stretch text-left hover:bg-stone-50 active:bg-stone-100 transition-colors cursor-pointer"
                  onClick={() => navigate('/activity/' + activity.id)}>
                  {/* Left: photo or emoji */}
                  <div className="flex items-center justify-center w-14 bg-stone-50 flex-shrink-0">
                    {activity.photo_url ? (
                      <img src={activity.photo_url} alt=""
                        className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm" />
                    ) : (
                      <span className="text-2xl">{type.emoji}</span>
                    )}
                  </div>
                  {/* Middle: info */}
                  <div className="flex-1 min-w-0 px-3 py-3">
                    <h3 className="font-bold text-gray-800 text-sm truncate">{activity.nickname}</h3>
                    <p className="text-xs text-stone-400 mt-0.5">
                      {type.label}
                      {activity.is_ysc ? ' · YSC' : ''}
                      {activity.current_grade ? ' · Gr.' + activity.current_grade : ''}
                      {activity.current_book ? ' · Book ' + activity.current_book : ''}
                      {activity.locality ? ' · ' + activity.locality : ''}
                    </p>
                    {activity.end_date && new Date(activity.end_date) < new Date() && (
                      <p className="text-xs text-amber-600 font-semibold mt-1">
                        📦 Ended {new Date(activity.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · Archive?
                      </p>
                    )}
                    {(activity.current_unit || activity.current_section || activity.current_lesson) && (
                      <p className="text-xs text-teal-600 mt-0.5">
                        {'Next: '}
                        {activity.current_unit ? 'Unit ' + activity.current_unit : ''}
                        {activity.current_section ? (activity.current_unit ? ' · ' : '') + 'Sec ' + activity.current_section : ''}
                        {activity.current_lesson ? 'Lesson ' + activity.current_lesson : ''}
                      </p>
                    )}
                  </div>
                  {/* Right: Log button */}
                  <button
                    onClick={e => {
                      e.stopPropagation()
                      navigate('/log/' + activity.id)
                    }}
                    className="bg-teal-700 hover:bg-teal-800 active:bg-teal-900 text-white text-xs font-bold px-3 flex flex-col items-center justify-center gap-0.5 flex-shrink-0 w-16 transition-colors">
                    <span className="leading-tight text-center text-[11px]">+ Log</span>
                  </button>
                </div>
              )
            })}
            {(() => { const filtered = activities.filter(a => !nucleusFilter || a.nucleus_id === nucleusFilter); return filtered.length > 5 && (
              <button onClick={() => setShowAllActivities(v => !v)}
                className="w-full text-center text-xs text-teal-600 font-semibold py-1.5 hover:text-teal-800">
                {showAllActivities ? '▲ Show less' : `▼ Show ${filtered.length - 5} more activities`}
              </button>
            )})()}
          </div>
        )}

        {/* ── COMMUNITY EVENTS — one-off events ──
            Only render the button + panel when there's at least one
            one-off home visit visible under the active nucleus filter.
            Empty state ("No one-off events logged yet") is now
            unreachable since the whole block is hidden in that case. */}
        {oneOffEvents.filter(s => !nucleusFilter || s.nucleus_id === nucleusFilter || s.recurring_activities?.nucleus_id === nucleusFilter).length > 0 && (
        <>
        <button onClick={() => setShowEventsPanel(v => !v)}
          className="w-full bg-white rounded-2xl px-4 py-3 shadow-sm flex items-center justify-between mb-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <span className="text-xl">🎉</span>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-700">Home Visits</p>
              <p className="text-xs text-stone-400">One-off home visits · {oneOffEvents.filter(s => !nucleusFilter || s.nucleus_id === nucleusFilter || s.recurring_activities?.nucleus_id === nucleusFilter).length} total</p>
            </div>
          </div>
          <span className={`text-stone-400 text-lg transition-transform duration-200 ${showEventsPanel ? 'rotate-90' : ''}`}>›</span>
        </button>

        {showEventsPanel && (
          <div className="space-y-2 mb-4 -mt-3">
            {oneOffEvents.length === 0 ? (
              <div className="bg-white rounded-2xl p-4 shadow-sm text-center">
                <p className="text-xs text-stone-400">No one-off events logged yet</p>
              </div>
            ) : (
              <>
                {(showAllEvents
                  ? oneOffEvents.filter(s => !nucleusFilter || s.nucleus_id === nucleusFilter || s.recurring_activities?.nucleus_id === nucleusFilter)
                  : oneOffEvents.filter(s => !nucleusFilter || s.nucleus_id === nucleusFilter || s.recurring_activities?.nucleus_id === nucleusFilter).slice(0, 5)
                ).map(s => {
                  const type = getType(s.activity_type)
                  const activityName = s.nickname || type.label
                  return (
                    <button key={s.id}
                      onClick={() => navigate('/session/' + s.id)}
                      className="w-full bg-white rounded-2xl shadow-sm overflow-hidden text-left hover:bg-stone-50 transition-colors flex items-center">
                      <div className="w-12 h-12 flex-shrink-0 overflow-hidden rounded-l-xl">
                        {s.photo_urls?.[0] ? (
                          <img src={s.photo_urls[0]} alt="" className="w-12 h-12 object-cover" />
                        ) : (
                          <div className="w-12 h-12 flex items-center justify-center bg-stone-50">
                            <span className="text-xl">{type.emoji}</span>
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1 px-3 py-2.5">
                        <p className="text-sm font-semibold text-gray-800 truncate">{activityName}</p>
                        <p className="text-xs text-stone-400">
                          {type.label} · {formatDate(s.date)}
                          {s.present_count > 0 ? ' · ' + s.present_count + ' present' : ''}
                        </p>
                      </div>
                      <span className="text-stone-300 text-sm flex-shrink-0 pr-3">›</span>
                    </button>
                  )
                })}
                {(() => { const filtered = oneOffEvents.filter(s => !nucleusFilter || s.nucleus_id === nucleusFilter || s.recurring_activities?.nucleus_id === nucleusFilter); return filtered.length > 5 && (
                  <button onClick={() => setShowAllEvents(v => !v)}
                    className="w-full text-center text-xs text-teal-600 font-semibold py-2 hover:text-teal-800">
                    {showAllEvents ? '▲ Show less' : `▼ Show all ${filtered.length} events`}
                  </button>
                )})()}
              </>
            )}
          </div>
        )}
        </>
        )}

        {/* ── MANAGE ALL ACTIVITIES ── */}
        <button onClick={() => setShowArchive(true)}
          className="w-full text-center text-xs text-stone-400 font-medium py-2 hover:underline mb-2">
          ··· Manage all activities
        </button>

        {/* ── RECENT SESSIONS — ongoing activities only ── */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-semibold text-stone-400 uppercase tracking-widest">Recent Sessions</h2>
          {recentSessions.filter(s => !nucleusFilter || s.nucleus_id === nucleusFilter || s.recurring_activities?.nucleus_id === nucleusFilter).length > 0 && (
            <span className="text-xs text-stone-400">{recentSessions.filter(s => !nucleusFilter || s.nucleus_id === nucleusFilter || s.recurring_activities?.nucleus_id === nucleusFilter).length} total</span>
          )}
        </div>

        {recentSessions.length === 0 ? (
          <div className="bg-white rounded-2xl p-4 shadow-sm text-center mb-4">
            <p className="text-xs text-stone-400">Sessions you log will appear here</p>
          </div>
        ) : (
          <div className="space-y-2 mb-4">
            {(showAllSessions
              ? recentSessions.filter(s => !nucleusFilter || s.nucleus_id === nucleusFilter || s.recurring_activities?.nucleus_id === nucleusFilter)
              : recentSessions.filter(s => !nucleusFilter || s.nucleus_id === nucleusFilter || s.recurring_activities?.nucleus_id === nucleusFilter).slice(0, 3)
            ).map(s => {
              const type = getType(s.activity_type)
              const activityName = s.activityName || s.nickname || type.label
              const progress = sessionProgressSummary(s)
              return (
                <button key={s.id}
                  onClick={() => navigate('/session/' + s.id)}
                  className="w-full bg-white rounded-2xl shadow-sm overflow-hidden text-left hover:bg-stone-50 transition-colors flex items-center">
                  <div className="w-12 h-12 flex-shrink-0 overflow-hidden rounded-l-xl">
                    {s.photo_urls?.[0] ? (
                      <img src={s.photo_urls[0]} alt="" className="w-12 h-12 object-cover" />
                    ) : (
                      <div className="w-12 h-12 flex items-center justify-center bg-stone-50">
                        <span className="text-xl">{type.emoji}</span>
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1 px-3 py-2.5">
                    <p className="text-sm font-semibold text-gray-800 truncate">{activityName}</p>
                    <p className="text-xs text-stone-400">
                      {formatDate(s.date)}
                      {s.present_count > 0 ? ' · ' + s.present_count + ' present' : ''}
                      {progress ? ' · ' + progress : ''}
                    </p>
                  </div>
                  <span className="text-stone-300 text-sm flex-shrink-0 pr-3">›</span>
                </button>
              )
            })}
            {(() => { const filtered = recentSessions.filter(s => !nucleusFilter || s.nucleus_id === nucleusFilter || s.recurring_activities?.nucleus_id === nucleusFilter); return filtered.length > 3 && (
              <button onClick={() => setShowAllSessions(v => !v)}
                className="w-full text-center text-xs text-teal-600 font-semibold py-2 hover:text-teal-800">
                {showAllSessions ? '▲ Show less' : `▼ Show all ${filtered.length} sessions`}
              </button>
            )})()}
          </div>
        )}

        {/* ── MEETINGS ── */}
        {meetings.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-3 mt-2">
              <h2 className="text-xs font-semibold text-stone-400 uppercase tracking-widest">Meetings</h2>
              <span className="text-xs text-stone-400">{meetings.length} total</span>
            </div>
            <div className="space-y-2 mb-4">
              {(showAllMeetings ? meetings : meetings.slice(0, 3)).map(s => (
                <button key={s.id}
                  onClick={() => navigate('/session/' + s.id)}
                  className="w-full bg-white rounded-2xl shadow-sm overflow-hidden text-left hover:bg-stone-50 transition-colors">
                  <div className="px-4 py-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-semibold text-gray-800">
                        🗓️ {s.activityName || 'Meeting'}
                      </p>
                      <p className="text-xs text-stone-400 flex-shrink-0 ml-2">{formatDate(s.date)}</p>
                    </div>
                    {s.notes ? (
                      <p className="text-xs text-stone-500 line-clamp-2 leading-relaxed">{s.notes}</p>
                    ) : (
                      <p className="text-xs text-stone-300 italic">No notes recorded</p>
                    )}
                    {s.guidance_studied && (
                      <p className="text-xs text-stone-500 mt-1 truncate">📖 {s.guidance_studied}</p>
                    )}
                    {s.note_attachment_url && (
                      <p className="text-xs text-teal-600 mt-1">📎 Notes attached</p>
                    )}
                  </div>
                </button>
              ))}
              {meetings.length > 3 && (
                <button onClick={() => setShowAllMeetings(v => !v)}
                  className="w-full text-center text-xs text-teal-600 font-semibold py-2 hover:text-teal-800">
                  {showAllMeetings ? '▲ Show less' : `▼ Show all ${meetings.length} meetings`}
                </button>
              )}
            </div>
          </>
        )}

      </div>

      {/* Log Activity FAB */}
      <div className="fixed bottom-20 left-0 right-0 flex justify-center pointer-events-none">
        <button onClick={() => navigate('/activities')}
          className="pointer-events-auto bg-teal-700 hover:bg-teal-800 text-white font-bold px-6 py-3 rounded-full shadow-lg text-sm transition-colors">
          + Create New Activity
        </button>
      </div>
      {/* Bottom nav */}
      <BottomNav />

      {/* Conflict-resolution modal — shown when handleApproveMerge detected
          column-level conflicts between the two participants. */}
      {pendingMerge && (
        <ConflictResolutionModal
          keep={pendingMerge.keep}
          drop={pendingMerge.drop}
          conflicts={pendingMerge.conflicts}
          onResolve={(resolutions) => finalizeMerge({
            keepId: pendingMerge.keepId,
            dropId: pendingMerge.dropId,
            suggestion: pendingMerge.suggestion,
            resolutions,
          })}
          onCancel={() => setPendingMerge(null)}
        />
      )}

      {/* Archive modal */}
      {showArchive && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-end justify-center z-50">
          <div className="bg-white rounded-t-2xl w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">Manage Activities</h2>
              <button onClick={() => setShowArchive(false)} className="text-stone-400 text-2xl leading-none">×</button>
            </div>
            <p className="text-xs text-stone-400 mb-4">Tap an activity to edit or archive it.</p>

            {/* Ongoing activities */}
            {activities.length > 0 && (
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-2">Ongoing Activities</p>
            )}
            {activities.length === 0 ? (
              <p className="text-sm text-stone-400 text-center py-4">No active activities.</p>
            ) : (
              <div className="space-y-2 mb-4">
                {activities.map(activity => {
                  const type = getType(activity.activity_type)
                  return (
                    <div key={activity.id} className="bg-stone-50 rounded-xl overflow-hidden">
                      {/* Activity name row - taps to edit */}
                      <button onClick={() => { setShowArchive(false); navigate('/activity/' + activity.id + '?edit=1') }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors text-left">
                        <span className="text-xl flex-shrink-0">{type.emoji}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">{activity.nickname}</p>
                          <p className="text-xs text-stone-400">{type.label}</p>
                        </div>
                        <span className="text-xs text-teal-600 font-medium flex-shrink-0">Edit ›</span>
                      </button>
                      {/* Action buttons */}
                      <div className="flex border-t border-stone-200">
                        <button onClick={() => archiveActivity(activity.id)}
                          className="flex-1 text-xs text-orange-500 hover:bg-orange-50 py-2.5 font-semibold transition-colors border-r border-stone-200">
                          📦 Archive
                        </button>
                        <button onClick={async () => {
                          if (window.confirm('Delete "' + activity.nickname + '"? This cannot be undone.')) {
                            const blocked = writeBlocked(await supabase.from('recurring_activities').update({ status: 'deleted', deleted_at: new Date().toISOString() }).eq('id', activity.id).select('id'))
                            if (blocked) { alert('Could not delete this activity: ' + blocked); return }
                            setShowArchive(false); fetchActivities()
                          }
                        }}
                          className="flex-1 text-xs text-red-500 hover:bg-red-50 py-2.5 font-semibold transition-colors">
                          🗑 Delete
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Community events */}
            {oneOffEvents.length > 0 && (<>
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-2 mt-2">Home Visits</p>
              <div className="space-y-2">
                {oneOffEvents.map(s => {
                  const type = getType(s.activity_type)
                  return (
                    <div key={s.id} className="bg-stone-50 rounded-xl overflow-hidden">
                      <button onClick={() => { setShowArchive(false); navigate('/session/' + s.id) }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-stone-100 transition-colors">
                        <span className="text-xl flex-shrink-0">{type.emoji}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">{s.nickname || s.activityName || type.label}</p>
                          <p className="text-xs text-stone-400">{type.label} · {formatDate(s.date)}</p>
                        </div>
                        <span className="text-xs text-teal-600 font-medium flex-shrink-0">Edit ›</span>
                      </button>
                      <div className="flex border-t border-stone-200">
                        <button onClick={async () => {
                          if (window.confirm('Delete this event? This cannot be undone.')) {
                            const now = new Date().toISOString()
                            const { data: attRows } = await supabase
                              .from('session_attendance')
                              .select('participant_id')
                              .eq('session_id', s.id)
                              .eq('present', true)
                            const assistantIds = (s.assistant_ids || [])
                            await supabase.from('activity_sessions')
                              .update({ deleted_at: now })
                              .eq('id', s.id)
                            await supabase.from('celebrations').delete().eq('session_id', s.id)
                            const presentIds = (attRows || []).map(r => r.participant_id).filter(Boolean)
                            const allAffectedIds = [...new Set([...presentIds, ...assistantIds])]
                            if (allAffectedIds.length > 0) {
                              try {
                                await recalculateProgress(allAffectedIds)
                              } catch (e) {
                                console.error('recalculate error', e)
                              }
                            }
                            setShowArchive(false)
                            fetchRecentSessions()
                          }
                        }} className="flex-1 text-xs text-red-500 hover:bg-red-50 py-2.5 font-semibold transition-colors">
                          🗑 Delete
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </>)}

            {/* Active Campaigns */}
            {activeCampaigns.length > 0 && (<>
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-2 mt-4">Active Campaigns</p>
              <div className="space-y-2">
                {activeCampaigns.map(camp => (
                  <div key={camp.id} className="bg-amber-50 rounded-xl overflow-hidden">
                    <button onClick={() => { setShowArchive(false); navigate('/campaigns/' + camp.id) }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-amber-100 transition-colors">
                      <span className="text-xl flex-shrink-0">🏷️</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{camp.name}</p>
                        {camp.locality && <p className="text-xs text-stone-400">📍 {camp.locality}</p>}
                      </div>
                      <span className="text-xs text-teal-600 font-medium flex-shrink-0">Manage ›</span>
                    </button>
                  </div>
                ))}
                <button onClick={() => { setShowArchive(false); navigate('/campaigns') }}
                  className="w-full text-center text-xs text-amber-600 font-semibold py-2 hover:text-amber-800">
                  View all campaigns ›
                </button>
              </div>
            </>)}
          </div>
        </div>
      )}


    </div>
  )
}